import { useEffect, useState } from "react";
import { Calendar, Check, AlertCircle, User } from "../../components/icons";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { ChangePasswordSection } from "../../components/ChangePasswordSection";

export function PartnerSettings() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [bookingUrl, setBookingUrl] = useState("");
  const [fullName, setFullName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    Promise.all([
      supabase
        .from("partners")
        .select("booking_url")
        .eq("user_id", user.id)
        .maybeSingle(),
      supabase
        .from("users")
        .select("full_name")
        .eq("id", user.id)
        .maybeSingle(),
    ]).then(([partnerRes, userRes]) => {
      setBookingUrl(partnerRes.data?.booking_url || "");
      setFullName(userRes.data?.full_name || user?.user_metadata?.full_name || "");
      setIsLoading(false);
    });
  }, [user?.id]);

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const { error: updateError } = await supabase
        .from("partners")
        .update({ booking_url: bookingUrl.trim() || null, updated_at: new Date().toISOString() })
        .eq("user_id", user.id);
      if (updateError) throw updateError;

      if (fullName.trim()) {
        const { error: userError } = await supabase
          .from("users")
          .update({ full_name: fullName.trim() })
          .eq("id", user.id);
        if (userError) throw userError;

        const { error: authError } = await supabase.auth.updateUser({
          data: { full_name: fullName.trim() },
        });
        if (authError) throw authError;
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading settings..." />;

  return (
    <div className="min-h-screen bg-brand-5 py-8 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-heading-sm text-gray-80">Settings</h1>
          <p className="text-text-md text-gray-50">Configure your partner account preferences</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-destructive-5 text-destructive-40 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="freud-card-elevated p-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2.5 bg-brand-10 rounded-xl flex-shrink-0">
              <User className="w-5 h-5 text-brand-40" />
            </div>
            <div>
              <h2 className="text-heading-xs text-gray-80">Profile Name</h2>
              <p className="text-text-sm text-gray-40">
                The name displayed on your partner account and shown to your coaches.
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                setSaved(false);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              placeholder="Your full name"
              className="freud-input flex-1"
            />
          </div>
        </div>

        <div className="mt-6 freud-card-elevated p-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2.5 bg-brand-10 rounded-xl flex-shrink-0">
              <Calendar className="w-5 h-5 text-brand-40" />
            </div>
            <div>
              <h2 className="text-heading-xs text-gray-80">Booking Page URL</h2>
              <p className="text-text-sm text-gray-40">
                The scheduling link your coaches see for their complimentary founder success session.
                Works with any platform — Calendly, GoHighLevel, Cal.com, etc.
              </p>
            </div>
          </div>

          <div className="mt-5">
            <input
              type="url"
              value={bookingUrl}
              onChange={(e) => {
                setBookingUrl(e.target.value);
                setSaved(false);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              placeholder="https://calendly.com/yourname/profile-success-session"
              className="freud-input w-full"
            />
          </div>

          {bookingUrl && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-text-xs text-gray-40">Current link:</span>
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-xs text-brand-40 hover:underline truncate max-w-xs"
              >
                {bookingUrl}
              </a>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="freud-btn-primary px-6 py-3 rounded-xl font-semibold whitespace-nowrap flex items-center gap-2"
          >
            {saved ? <Check className="w-4 h-4" /> : null}
            {saving ? "Saving..." : saved ? "Saved!" : "Save Changes"}
          </button>
        </div>

        <div className="mt-6">
          <ChangePasswordSection />
        </div>
      </div>
    </div>
  );
}
